import { Type, type SchemaOptions } from '@sinclair/typebox';
import { customType } from 'drizzle-orm/pg-core';
import { TypeID, typeid } from 'typeid-js';

const typeIdLength = 26;

export const idTypes = {
  account: 'acc',
  accountScope: 'scope',
  accountHistory: 'ah',
  user: 'u',
  dashboardSession: 'ds',
  app: 'app',
  webhookConversation: 'wc',
  webhookEndpoint: 'we',
  webhookEndpointSecret: 'wes',
  event: 'evt',
  apiKey: 'pk',
  realtimeInstance: 'ri',
  organization: 'org',
} as const;

type IdType = typeof idTypes;
type ReversedIdType = { [K in keyof IdType as IdType[K]]: K };

const reversedIdTypes = Object.fromEntries(Object.entries(idTypes).map(([x, y]) => [y, x])) as ReversedIdType;

type IdTypePrefixes = keyof typeof idTypes;
export type TypeId<T extends IdTypePrefixes> = `${IdType[T]}_${string}`;

export const TypeIdSchema = <T extends IdTypePrefixes>(prefix: T, opts?: SchemaOptions) =>
  Type.TemplateLiteral([Type.Literal(idTypes[prefix]), Type.Literal('_'), Type.String()], opts);

export const typeIdGenerator = <const T extends IdTypePrefixes>(prefix: T) => typeid(idTypes[prefix]).toString() as TypeId<T>;

export const typeIdDataType = <const T extends IdTypePrefixes>(prefix: T, column: string) =>
  customType<{
    data: TypeId<T>;
    notNull: true;
    driverData: string;
  }>({
    dataType: () => `char(${typeIdLength + idTypes[prefix].length + 1})`, // suffix length + prefix length + underscore
    fromDriver: (input) => TypeID.fromString(input).toString() as TypeId<T>,
    toDriver: (input) => input?.toString(),
  })(column);

export const validateTypeId = <const T extends IdTypePrefixes>(prefix: T, data: unknown): data is TypeId<T> =>
  typeof data === 'string' && data.startsWith(`${idTypes[prefix]}_`) && data.length === idTypes[prefix].length + typeIdLength + 1;

export const inferTypeId = <T extends keyof ReversedIdType>(input: `${T}_${string}`) =>
  reversedIdTypes[TypeID.fromString(input).getType() as T];
